import { SECTION_DESCRIPTIONS } from '@communities/community/utils/sectionDescriptions';
import { Text, Heading, Dialog } from '@soteria/design-system-ve';

import * as styles from './MoreInfoDialog.css';

interface AuthenticationProvidersMoreInfoDialogProps {
  isOpen: boolean;
  onClose: () => void;
}

export function AuthenticationProvidersMoreInfoDialog({ isOpen, onClose }: AuthenticationProvidersMoreInfoDialogProps) {
  return (
    <Dialog cancelAction={{ label: 'Close' }} isOpen={isOpen} size="xxxl" title={SECTION_DESCRIPTIONS.AUTHENTICATION_PROVIDERS.title} onClose={onClose}>
      <div className={styles.dialogContent}>
        <Text>
          Authentication providers enable secure access to your community groups and their resources by connecting external identity systems (like Azure AD,
          Google, or other OAuth2 providers) with the platform.
        </Text>

        <div className={styles.section}>
          <Heading level="h3">How Authentication Providers Work:</Heading>
          <ul className={styles.list}>
            <li className={styles.listItem}>
              <Text>
                <strong>Secure Access Control</strong> - Users must authenticate through registered providers before accessing community resources
              </Text>
            </li>
            <li className={styles.listItem}>
              <Text>
                <strong>Identity Validation</strong> - The system validates user identity and permissions through external OAuth2 providers
              </Text>
            </li>
            <li className={styles.listItem}>
              <Text>
                <strong>Multi-Organization Support</strong> - Different community groups can use different authentication providers based on their needs
              </Text>
            </li>
            <li className={styles.listItem}>
              <Text>
                <strong>API Protection</strong> - Only pre-approved OAuth2 applications can access your community's APIs and resources
              </Text>
            </li>
          </ul>
        </div>

        <div className={styles.section}>
          <Heading level="h3">Key Features:</Heading>
          <ul className={styles.list}>
            <li className={styles.listItem}>
              <Text>
                <strong>Automatic Configuration</strong> - System fetches OpenID Connect settings from provider URLs for seamless integration
              </Text>
            </li>
            <li className={styles.listItem}>
              <Text>
                <strong>Role-Based Access</strong> - Works with the platform's role-based access system to determine user permissions
              </Text>
            </li>
            <li className={styles.listItem}>
              <Text>
                <strong>Approval Workflow</strong> - New providers start with "Pending" status and require system administrator approval
              </Text>
            </li>
            <li className={styles.listItem}>
              <Text>
                <strong>Flexible Management</strong> - System administrators can manage all providers, while regular users can create providers that require
                approval
              </Text>
            </li>
          </ul>
        </div>

        <div className={styles.section}>
          <Heading level="h3">Provider Status:</Heading>
          <ul className={styles.list}>
            <li className={styles.listItem}>
              <Text>
                <strong>Pending</strong> - Newly created providers awaiting system administrator approval
              </Text>
            </li>
            <li className={styles.listItem}>
              <Text>
                <strong>Approved</strong> - Active providers ready for use by community groups
              </Text>
            </li>
            <li className={styles.listItem}>
              <Text>
                <strong>Default</strong> - The primary authentication provider for the community group
              </Text>
            </li>
          </ul>
        </div>

        <Text>Authentication providers are essential for maintaining secure, scalable access control across your community ecosystem.</Text>
      </div>
    </Dialog>
  );
}
